import { Component, inject, OnInit, signal, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ApplicationFormService } from '../../services/application-form/application-form.service';
import { ToastService } from '../../services/general/toast.service';
import { ApplicationForm, ApplicationFormQueryParams } from '../../interfaces/responses/application-form.interface';
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { ProgressLoaderComponent } from '../../components/loaders/progress-loader/progress-loader.component';
import { ConfirmationDialogComponent } from '../../components/shared/confirmation-dialog/confirmation-dialog.component';
import { PermissionService } from '../../services/permission/permission.service';

@Component({
  selector: 'app-application-forms',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PaginationComponent, ProgressLoaderComponent, ConfirmationDialogComponent],
  templateUrl: './application-forms.component.html',
  styleUrl: './application-forms.component.scss',
})
export class ApplicationFormsComponent implements OnInit {
  private readonly _formService = inject(ApplicationFormService);
  permissionService = inject(PermissionService);
  private readonly _toast = inject(ToastService);
  private readonly _router = inject(Router);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _fb = inject(FormBuilder);

  readonly isLoading = signal(false);
  readonly forms = signal<ApplicationForm[]>([]);
  readonly formsResponse = signal<PaginationInterface<ApplicationForm[]> | null>(null);
  readonly deletingFormId = signal<string | null>(null);
  readonly formToDelete = signal<ApplicationForm | null>(null);
  viewMode: 'table' | 'grid' = 'table';

  readonly queryParams = signal<ApplicationFormQueryParams>({
    page: 1,
    perPage: 10,
    paginate: true,
  });

  readonly searchQuery = this._fb.group({
    keyword: [''],
  });

  ngOnInit(): void {
    this.loadForms();

    if (isPlatformBrowser(this._platformId)) {
      this.searchQuery.get('keyword')?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((keyword: string | null) => {
          this.queryParams.update(params => ({
            ...params,
            keyword: (keyword || '').trim(),
            page: 1,
          }));
          this.loadForms();
        });
    }
  }

  loadForms(): void {
    this.isLoading.set(true);
    this._formService.getForms(this.queryParams()).subscribe({
      next: (response) => {
        this.formsResponse.set(response);
        this.forms.set(response.data ?? []);
        this.isLoading.set(false);
      },
      error: () => {
        this._toast.error('Failed to load application forms');
        this.isLoading.set(false);
      },
    });
  }

  navigateToNew(): void {
    this._router.navigate(['/application-forms/new']);
  }

  navigateToEdit(id: string): void {
    this._router.navigate(['/application-forms', id, 'edit']);
  }

  copyLink(token: string): void {
    if (!isPlatformBrowser(this._platformId)) return;
    const link = `${window.location.origin}/apply/${token}`;
    navigator.clipboard.writeText(link).then(() => {
      this._toast.success('Link copied to clipboard');
    }).catch(() => {
      this._toast.error('Failed to copy link to clipboard');
    });
  }

  toggleActive(form: ApplicationForm): void {
    this._formService.updateForm(form.id, { isActive: !form.isActive }).subscribe({
      next: () => {
        this._toast.success(`Form ${form.isActive ? 'deactivated' : 'activated'} successfully`);
        this.loadForms();
      },
      error: () => this._toast.error('Failed to update form status'),
    });
  }

  promptDelete(form: ApplicationForm): void {
    this.formToDelete.set(form);
  }

  cancelDelete(): void {
    this.formToDelete.set(null);
  }

  confirmDelete(): void {
    const form = this.formToDelete();
    if (!form) return;
    this.deletingFormId.set(form.id);
    this.formToDelete.set(null);
    this._formService.deleteForm(form.id).subscribe({
      next: () => {
        this._toast.success('Form deleted successfully');
        this.deletingFormId.set(null);
        this.loadForms();
      },
      error: () => {
        this._toast.error('Failed to delete form');
        this.deletingFormId.set(null);
      },
    });
  }

  formLabel(form: ApplicationForm): string {
    return form.name || `${form.institutionName || form.institutionId} — ${form.productName || form.productId}`;
  }

  onPageChange(page: number): void {
    this.queryParams.update(p => ({ ...p, page }));
    this.loadForms();
  }
}
